import { Fragment, useState, useEffect, useCallback } from 'react'
import { X, Zap, DollarSign, Loader2, Check, Shield, RefreshCw, Copy, KeyRound, ChevronDown, ChevronRight, AlertTriangle, FileText, Bot, BookOpen, Sparkles, Activity, Play as PlayIcon } from 'lucide-react'
import ReviewLabelsModal from './ReviewLabelsModal'
import { useHighlightsApi } from './useHighlightsApi'

// Settings panel for tbtv-highlights. Surfaces the paired Mux + TBTV-AI
// auto-trigger toggles. Any future settings just need a TOGGLE_META entry.

const TOGGLE_META = {
  // ---- Master switch (migration 0042) ----
  // Turning this OFF disables auto-fire for EVERY detector below. The
  // per-detector toggles keep their state — they just don't run until
  // this master switch is ON again. Manual runs via the Run AI modal are
  // unaffected. Use when investigating quality issues or when captions
  // for a batch should be reviewed before AI touches them.
  robots_auto_fire_after_ingest: {
    family: 'master',
    label: 'Auto-run AI after captions ready',
    icon: <Zap size={16} />,
    description:
      'Master switch — when OFF, no detector auto-runs when a caption ingest completes. Editors run each video\'s AI manually via the Run AI modal (unaffected by this toggle). Per-detector toggles below still control which detectors run under the master when it\'s ON. Default ON.',
  },
  // ---- Mux Robots (paid per minute + per job) ----
  robots_find_key_moments_enabled: {
    family: 'mux',
    detectorKey: 'mux_find_key_moments',
    label: 'Mux Find Key Moments',
    icon: <Zap size={16} />,
    description:
      'Multi-modal AI from Mux that scans audio + visual + transcript. Sport-agnostic, hard-capped at 10 moments per video. Disabled by default after migration 0010 — TBTV-AI Find Key Moments is the replacement.',
  },
  robots_generate_chapters_enabled: {
    family: 'mux',
    detectorKey: 'mux_generate_chapters',
    label: 'Mux Generate Chapters',
    icon: <BookOpen size={16} />,
    description: 'Mux\'s transcript-derived chapter list. Useful as a second voice alongside TBTV-AI chapters.',
  },
  robots_summarize_enabled: {
    family: 'mux',
    detectorKey: 'mux_summarize',
    label: 'Mux Summarize',
    icon: <FileText size={16} />,
    description: 'Mux\'s auto-generated title + description + tags.',
  },
  // ---- TBTV-AI (Claude + keyword) ----
  tbtv_ai_find_key_moments_enabled: {
    family: 'tbtv_ai',
    detectorKey: 'ai_find_key_moments_keyword',
    label: 'TBTV-AI Find Key Moments',
    icon: <Bot size={16} />,
    description:
      'Sport-aware caption-keyword scan (free). Toggle controls whether this detector auto-runs after captions become ready. Manual runs from the Run AI modal work regardless of this toggle. Optionally augmented by the Claude LLM sublayer below for narrative beats.',
  },
  tbtv_ai_find_key_moments_llm: {
    family: 'tbtv_ai',
    detectorKey: 'ai_find_key_moments_llm',
    label: 'TBTV-AI Find Key Moments — LLM sublayer',
    icon: <Sparkles size={16} />,
    description:
      'Claude reads the full transcript + existing summary and identifies every goal, save, fight, and narrative turning point with player-name granularity. Adds rows beyond keyword scan; deduped within ±5s.',
    indent: true,
  },
  tbtv_ai_generate_chapters_enabled: {
    family: 'tbtv_ai',
    detectorKey: 'ai_generate_chapters',
    label: 'TBTV-AI Generate Chapters',
    icon: <BookOpen size={16} />,
    description: 'Claude segments the transcript into pre-game / per-period / intermission / post-game chapters matching the broadcast structure.',
  },
  tbtv_ai_summarize_enabled: {
    family: 'tbtv_ai',
    detectorKey: 'ai_summarize',
    label: 'TBTV-AI Summarize',
    icon: <Sparkles size={16} />,
    description: 'Claude writes an editorial title, paragraph description (with goal scorers + goaltender names), and 5–12 tags.',
  },
  // ---- Step 20: alias scanner ----
  claude_alias_scan_enabled: {
    family: 'tbtv_ai',
    detectorKey: 'claude_alias_scan',
    label: 'Claude alias scanner',
    icon: <Bot size={16} />,
    description:
      'Claude sweeps every captioned transcript to find clusters of STT-mangled proper nouns (e.g. "kazerski" / "k-rosky" / "casarosky" all = "kaczorowski") and proposes canonical→alias mappings. Approved aliases expand every future search at zero per-query cost.',
  },
  // ---- Step 35 Phase 2: sport profiles ----
  // Diagnostic only — no detector, so it never counts as a paid toggle.
  tbtv_ai_debug_profile_dispatch: {
    family: 'tbtv_ai',
    label: 'Debug: log sport-profile dispatch',
    icon: <Activity size={16} />,
    description:
      'Logs one line per TBTV-AI job naming the sport profile it resolved (and whether it came from the video, the global default, or an explicit override) plus the prompt + vocab bundles that profile pulled in. Default OFF — turn it on while rolling out a new sport or when a video\'s results look like the wrong sport\'s rules were applied.',
  },
}

const JOB_LABEL = {
  find_key_moments: 'Find Key Moments',
  generate_chapters: 'Generate Chapters',
  summarize: 'Summarize',
  ai_find_key_moments: 'TBTV-AI Find Key Moments',
  ai_generate_chapters: 'TBTV-AI Generate Chapters',
  ai_summarize: 'TBTV-AI Summarize',
}

// Estimate cost for a single 2-hour game using the live pricing data.
// Mirrors src/pricing.js estimateJobCost on the worker side so the UI
// number lines up with what the worker actually bills.
function estimatePerGameCost(detectorKey, pricing) {
  if (!pricing) return null
  const d = pricing.detectors?.[detectorKey]
  if (!d) return null
  if (d.pricing_model === 'free') return 0
  const durationMin = 120
  if (d.pricing_model === 'mux') return durationMin * d.per_min + d.per_job
  if (d.pricing_model === 'llm') {
    const mp = pricing.llm_models?.[pricing.active_llm_model]
    if (!mp) return null
    return (durationMin * d.token_in_per_min / 1e6) * mp.input_per_million
         + (d.token_out_per_call / 1e6) * mp.output_per_million
  }
  return null
}

// Project monthly cost = per-game cost × videos-per-month from ingest_rate.
// Falls back to per-minute math when ingest data has total_minutes.
function projectMonthlyCost(detectorKey, pricing) {
  if (!pricing) return null
  const d = pricing.detectors?.[detectorKey]
  if (!d) return null
  if (d.pricing_model === 'free') return 0
  const minutes = pricing.ingest_rate?.total_minutes
  const games = pricing.ingest_rate?.video_count
  if (d.pricing_model === 'mux' && minutes != null && games != null) {
    return minutes * d.per_min + games * d.per_job
  }
  if (d.pricing_model === 'llm' && minutes != null && games != null) {
    const mp = pricing.llm_models?.[pricing.active_llm_model]
    if (!mp) return null
    return (minutes * d.token_in_per_min / 1e6) * mp.input_per_million
         + (games * d.token_out_per_call / 1e6) * mp.output_per_million
  }
  return null
}

function fmtUsd(n) {
  if (n == null) return '—'
  if (n === 0) return 'Free'
  if (n < 0.01) return `$${n.toFixed(4)}`
  return `$${n.toFixed(2)}`
}

export default function SettingsEditor({ onClose }) {
  const api = useHighlightsApi()
  const [data, setData] = useState(null)
  const [pricing, setPricing] = useState(null)
  const [err, setErr] = useState(null)
  const [busyKey, setBusyKey] = useState(null)
  // Confirm flow when turning ON a paid toggle.
  const [pendingEnable, setPendingEnable] = useState(null) // { key, currentValue }

  const load = useCallback(async () => {
    setErr(null)
    try {
      const r = await api.listSettings()
      setData(r)
    } catch (e) {
      setErr(e.message)
    }
  }, [api])

  useEffect(() => { load() }, [load])
  // Pricing + ingest rate. Fetched once; the worker recomputes the 30-day
  // rate on every call so it's never far stale anyway.
  useEffect(() => {
    api.adminPricing()
      .then(p => setPricing(p))
      .catch(() => { /* non-fatal — UI just shows '—' for costs */ })
  }, [])

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  // Turning ON a paid toggle goes through a confirm modal so editors
  // see the cost before incurring spend. Turning OFF is silent (no risk).
  function isPaidToggle(key) {
    const meta = TOGGLE_META[key]
    if (!meta) return false
    const d = pricing?.detectors?.[meta.detectorKey]
    return d && d.pricing_model !== 'free'
  }

  async function toggle(key, currentValue) {
    const turningOn = currentValue !== 'true'
    if (turningOn && isPaidToggle(key)) {
      setPendingEnable({ key, currentValue })
      return
    }
    await applyToggle(key, currentValue)
  }

  async function applyToggle(key, currentValue) {
    setBusyKey(key)
    const next = currentValue === 'true' ? 'false' : 'true'
    setData(d => ({
      ...d,
      settings: d.settings.map(s => s.key === key ? { ...s, value: next, updated_at: new Date().toISOString() } : s),
    }))
    try {
      await api.setSetting(key, next)
      await load()
    } catch (e) {
      setData(d => ({
        ...d,
        settings: d.settings.map(s => s.key === key ? { ...s, value: currentValue } : s),
      }))
      alert(`Failed to update ${key}: ${e.message}`)
    } finally {
      setBusyKey(null)
    }
  }

  const settings = data?.settings || []
  const knownToggles = settings.filter(s => TOGGLE_META[s.key])
  const capSetting = settings.find(s => s.key === 'robots_daily_job_cap')
  const aliasThresholdSetting = settings.find(s => s.key === 'claude_alias_scan_auto_apply_threshold')
  const rewriteModeSetting = settings.find(s => s.key === 'tbtv_ai_search_rewrite_mode')
  const cueSpanModeSetting = settings.find(s => s.key === 'tbtv_ai_llm_cue_span_mode')
  const NON_TOGGLE_KEYS = new Set([
    'robots_daily_job_cap',
    'claude_alias_scan_auto_apply_threshold',
    'tbtv_ai_search_rewrite_mode',
    'tbtv_ai_llm_cue_span_mode',
    'tbtv_ai_keyword_min_offset_ms',
    'tbtv_ai_keyword_bracket_filter_enabled',
    'tbtv_ai_keyword_recap_filter_enabled',
    'tbtv_ai_keyword_llm_verify_enabled',
    'tbtv_ai_keyword_verifier_max_usd_per_video',
  ])
  const unknownToggles = settings.filter(s => !TOGGLE_META[s.key] && !NON_TOGGLE_KEYS.has(s.key))

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.() }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
    >
      <div style={{ background: '#111', border: '1px solid #2a2a2a', borderRadius: 8, width: 'min(960px, 96vw)', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #2a2a2a', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>Settings</div>
            <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
              Each toggle controls whether the detector <strong>auto-runs</strong> when captions become ready for a new video. Toggles OFF do <em>not</em> hide the detector from the Run AI modal — editors can always fire any detector manually per video. Changes take effect on the next caption ingest; in-flight jobs continue; already-stored results are unaffected.
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#888', cursor: 'pointer', padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          {err && <div style={{ color: 'crimson', marginBottom: 12 }}>{err}</div>}
          {!data && !err && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#888', padding: 24, justifyContent: 'center' }}>
              <Loader2 size={14} className="spin" /> Loading…
            </div>
          )}

          {/* Step 27 UI: eval panel. Always visible. Shows the latest
              eval_runs row + lets editorial trigger a fresh run from
              here without dropping to the CLI. Empty state guides the
              editor to start approving moments. */}
          <EvalPanel />

          {/* Step 29 UI: RAG exemplar panel. Shows how many of the editor's
              recent drops + approves are being injected into each LLM call. */}
          <RagPanel />

          {/* Step 26.13 UI: stuck + errored job recovery. */}
          <JobRecoveryPanel />

          {/* Step 28A Phase 2b: entity-resolver backfill driver. */}
          <EntityBackfillPanel />

          {/* Step 35 Phase 4: sport-profile admin panel (list + inline edit + delete).
              Creating a new profile still requires a code deploy for the prompt
              bundle + vocab JSON, so the "Add" flow lives in the Phase 5 football
              rollout — until then this panel is view + edit only. */}
          <SportProfilesPanel />

          {/* dev25: reel-transition CRUD + bumper creator now live on the
              dedicated Highlights > "Reel Transitions" tab. Extracted out of
              this settings modal to reduce clutter and give editors a proper
              workspace with a Bumper Creator flow. */}

          {knownToggles.length > 0 && (
            <>
              {knownToggles.filter(s => TOGGLE_META[s.key].family === 'master').length > 0 && (
                <>
                  <SectionHeader title="AI auto-fire" />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                    {knownToggles.filter(s => TOGGLE_META[s.key].family === 'master').map(s => (
                      <ToggleCard key={s.key} s={s} pricing={pricing}
                        busyKey={busyKey} onToggle={toggle} />
                    ))}
                  </div>
                </>
              )}
              <SectionHeader title="Mux Robots" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                {knownToggles.filter(s => TOGGLE_META[s.key].family === 'mux').map(s => (
                  <ToggleCard key={s.key} s={s} pricing={pricing}
                    busyKey={busyKey} onToggle={toggle} />
                ))}
              </div>
              <SectionHeader title="TBTV-AI" subtitle={pricing?.active_llm_model ? `Active model: ${pricing.active_llm_model}` : null} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {knownToggles.filter(s => TOGGLE_META[s.key].family === 'tbtv_ai').map(s => (
                  <ToggleCard key={s.key} s={s} pricing={pricing}
                    busyKey={busyKey} onToggle={toggle} />
                ))}
              </div>
            </>
          )}

          {capSetting && (
            <DailyCapInput
              setting={capSetting}
              busy={busyKey === capSetting.key}
              onSave={async (newValue) => {
                setBusyKey(capSetting.key)
                try {
                  await api.setSetting(capSetting.key, String(newValue))
                  await load()
                } catch (e) {
                  alert(`Failed to update cap: ${e.message}`)
                } finally {
                  setBusyKey(null)
                }
              }}
            />
          )}

          {aliasThresholdSetting && (
            <AliasThresholdInput
              setting={aliasThresholdSetting}
              busy={busyKey === aliasThresholdSetting.key}
              onSave={async (newValue) => {
                setBusyKey(aliasThresholdSetting.key)
                try {
                  await api.setSetting(aliasThresholdSetting.key, String(newValue))
                  await load()
                } catch (e) {
                  alert(`Failed to update threshold: ${e.message}`)
                } finally {
                  setBusyKey(null)
                }
              }}
            />
          )}

          {rewriteModeSetting && (
            <RewriteModeSelector
              setting={rewriteModeSetting}
              busy={busyKey === rewriteModeSetting.key}
              onSave={async (newValue) => {
                setBusyKey(rewriteModeSetting.key)
                try {
                  await api.setSetting(rewriteModeSetting.key, newValue)
                  await load()
                } catch (e) {
                  alert(`Failed to update rewrite mode: ${e.message}`)
                } finally {
                  setBusyKey(null)
                }
              }}
            />
          )}

          {cueSpanModeSetting && (
            <CueSpanModeSelector
              setting={cueSpanModeSetting}
              busy={busyKey === cueSpanModeSetting.key}
              onSave={async (newValue) => {
                setBusyKey(cueSpanModeSetting.key)
                try {
                  await api.setSetting(cueSpanModeSetting.key, newValue)
                  await load()
                } catch (e) {
                  alert(`Failed to update cue span mode: ${e.message}`)
                } finally {
                  setBusyKey(null)
                }
              }}
            />
          )}

          <SecretRotation verify={api.listSettings} />

          {unknownToggles.length > 0 && (
            <details style={{ marginTop: 16 }}>
              <summary style={{ color: '#888', fontSize: 12, cursor: 'pointer' }}>
                Other settings ({unknownToggles.length})
              </summary>
              <pre style={{ fontSize: 11, marginTop: 8, background: '#000', padding: 8, borderRadius: 4 }}>
                {JSON.stringify(unknownToggles, null, 2)}
              </pre>
            </details>
          )}
        </div>

        {/* Footer: cost-watch */}
        {data?.jobs_this_month && (
          <div style={{ padding: '10px 16px', borderTop: '1px solid #1f1f1f', fontSize: 11, color: '#888', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {/* In-flight (right now) */}
            {data.in_flight && Object.keys(data.in_flight).length > 0 && (
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <span className="pulse" style={{ color: '#d4a72c' }}>🟡 In flight now:</span>
                {Object.entries(data.in_flight).map(([type, n]) => (
                  <span key={type} style={{ display: 'inline-flex', gap: 4 }}>
                    <strong style={{ color: '#bbb' }}>{JOB_LABEL[type] || type}:</strong>
                    {n}
                  </span>
                ))}
              </div>
            )}
            {/* Month-to-date */}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <span>This month:</span>
              {Object.keys(data.jobs_this_month).length === 0 && <span>no robot jobs yet</span>}
              {Object.entries(data.jobs_this_month).map(([type, stats]) => {
                const total = Object.values(stats).reduce((a, b) => a + b, 0)
                const completed = stats.completed || 0
                const errored = stats.errored || 0
                return (
                  <span key={type} style={{ display: 'inline-flex', gap: 6 }}>
                    <strong style={{ color: '#bbb' }}>{JOB_LABEL[type] || type}:</strong>
                    {total} job{total === 1 ? '' : 's'}
                    {completed > 0 && <span style={{ color: '#22c55e' }}>· {completed} <Check size={10} style={{ display: 'inline' }} /></span>}
                    {errored > 0 && <span style={{ color: '#dc2626' }}>· {errored} errored</span>}
                  </span>
                )
              })}
            </div>
          </div>
        )}

        <style>{`@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } } .spin { animation: spin 1s linear infinite; } @keyframes pulse { 0%,100% { opacity: 1 } 50% { opacity: 0.4 } } .pulse { animation: pulse 1.5s ease-in-out infinite; }`}</style>
      </div>

      {pendingEnable && (
        <ConfirmEnableModal
          settingKey={pendingEnable.key}
          pricing={pricing}
          onCancel={() => setPendingEnable(null)}
          onConfirm={async () => {
            const k = pendingEnable.key
            const cv = pendingEnable.currentValue
            setPendingEnable(null)
            await applyToggle(k, cv)
          }}
        />
      )}
    </div>
  )
}

function SectionHeader({ title, subtitle }) {
  return (
    <div style={{ marginBottom: 8, marginTop: 4, display: 'flex', alignItems: 'baseline', gap: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#bbb', textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</div>
      {subtitle && <div style={{ fontSize: 11, color: '#666', fontFamily: 'monospace' }}>{subtitle}</div>}
    </div>
  )
}

function ToggleCard({ s, pricing, busyKey, onToggle }) {
  const meta = TOGGLE_META[s.key]
  const on = s.value === 'true'
  const isBusy = busyKey === s.key
  const perGame = estimatePerGameCost(meta.detectorKey, pricing)
  const monthly = projectMonthlyCost(meta.detectorKey, pricing)
  return (
    <div style={{
      padding: 12,
      border: '1px solid ' + (on ? '#3b82f6' : '#333'),
      borderRadius: 6,
      background: on ? 'rgba(59,130,246,0.06)' : 'transparent',
      display: 'flex',
      gap: 12,
      alignItems: 'flex-start',
      marginLeft: meta.indent ? 20 : 0,
    }}>
      <div style={{ marginTop: 4, color: on ? '#3b82f6' : '#666' }}>{meta.icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{meta.label}</div>
          <span title={on
              ? 'Auto-runs after captions become ready for new videos'
              : 'Does NOT auto-run on caption ingest. Still available manually via the Run AI modal.'}
            style={{
              fontSize: 11, padding: '1px 8px', borderRadius: 10,
              background: on ? '#1e3a8a' : '#333',
              color: on ? '#bfdbfe' : '#888',
              cursor: 'help',
            }}>{on ? 'AUTO-RUN' : 'MANUAL ONLY'}</span>
          <span style={{ fontSize: 11, color: '#888' }}>
            {fmtUsd(perGame)}/2-hr game
            {monthly != null && monthly > 0 && (
              <span style={{ color: '#666' }}> · ~{fmtUsd(monthly)}/mo projected</span>
            )}
          </span>
        </div>
        <div style={{ fontSize: 11, color: '#aaa', marginBottom: 6 }}>{meta.description}</div>
        <div style={{ fontSize: 10, color: '#666', fontFamily: 'monospace' }}>
          {s.key} · last changed {s.updated_at || 'never'}{s.updated_by ? ` by ${s.updated_by}` : ''}
        </div>
      </div>
      <button
        onClick={() => onToggle(s.key, s.value)}
        disabled={isBusy}
        style={{
          padding: '6px 14px',
          background: on ? '#dc2626' : '#3b82f6',
          border: 'none', borderRadius: 4, color: 'white',
          cursor: 'pointer', fontWeight: 500, minWidth: 96,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4,
        }}>
        {isBusy ? <Loader2 size={13} className="spin" /> : (on ? 'Disable' : 'Enable')}
      </button>
    </div>
  )
}

function ConfirmEnableModal({ settingKey, pricing, onCancel, onConfirm }) {
  const meta = TOGGLE_META[settingKey]
  const perGame = estimatePerGameCost(meta.detectorKey, pricing)
  const monthly = projectMonthlyCost(meta.detectorKey, pricing)
  const ingestCount = pricing?.ingest_rate?.video_count
  const ingestWindow = pricing?.ingest_rate?.window_days
  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onCancel?.() }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001 }}
    >
      <div style={{ background: '#111', border: '1px solid #d4a72c', borderRadius: 8, width: 'min(480px, 96vw)', padding: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <AlertTriangle size={18} style={{ color: '#d4a72c' }} />
          <div style={{ fontSize: 15, fontWeight: 600 }}>Enable {meta.label}?</div>
        </div>
        <div style={{ fontSize: 12, color: '#bbb', marginBottom: 12, lineHeight: 1.5 }}>{meta.description}</div>
        <div style={{
          background: '#0d1622', border: '1px solid #2a2a2a', borderRadius: 6,
          padding: 12, marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ color: '#888' }}>Estimated cost per 2-hr game</span>
            <strong>{fmtUsd(perGame)}</strong>
          </div>
          {ingestCount != null && (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#888' }}>Recent ingest pace</span>
              <span>{ingestCount} videos in past {ingestWindow}d</span>
            </div>
          )}
          {monthly != null && monthly > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #2a2a2a', paddingTop: 6, marginTop: 2 }}>
              <span style={{ color: '#888' }}>Projected monthly cost</span>
              <strong style={{ color: '#d4a72c' }}>~{fmtUsd(monthly)}</strong>
            </div>
          )}
        </div>
        <div style={{ fontSize: 11, color: '#888', marginBottom: 14, fontStyle: 'italic' }}>
          Charges accrue per video as captions complete. You can disable this toggle anytime to halt new spend. In-flight jobs continue to completion. Existing data is unaffected.
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onCancel} style={{
            padding: '8px 14px', background: 'transparent', border: '1px solid #333', borderRadius: 4,
            color: 'inherit', cursor: 'pointer', fontSize: 13,
          }}>Cancel</button>
          <button onClick={onConfirm} style={{
            padding: '8px 16px', background: '#3b82f6', border: 'none', borderRadius: 4,
            color: 'white', cursor: 'pointer', fontWeight: 500, fontSize: 13,
          }}>Enable</button>
        </div>
      </div>
    </div>
  )
}

// ----------------------------------------------------------------------------
// SecretRotation — guided UI to rotate HIGHLIGHTS_SHARED_SECRET.
//
// Cloudflare worker secrets can only be installed via `wrangler secret put`
// (no API to write them remotely). So this UI doesn't talk to the worker to
// install — it just helps the operator by:
//   1. generating a strong random value locally (Web Crypto)
//   2. showing the exact two commands (highlights = Worker, vader = Pages)
//   3. one-click copy on the secret and each command
//   4. "Verify" button that re-calls /settings; success means both workers
//      now hold the same secret.
//
// Important: the generated value is held in component state only, never sent
// over the network. The user must copy + paste into wrangler manually.
// ----------------------------------------------------------------------------

function genSecret() {
  const bytes = new Uint8Array(32)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
}

const HIGHLIGHTS_PATH = '/Users/toddkaczorowski/Documents/tbtv-dev/TenbandTV2.0/web/tbtv-highlights'
const VADER_PATH      = '/Users/toddkaczorowski/Documents/tbtv-dev/TenbandTV2.0/web/tbtv-vader'

function SecretRotation({ verify }) {
  const [expanded, setExpanded] = useState(false)
  const [secret, setSecret] = useState(null)
  const [verifyState, setVerifyState] = useState('idle') // 'idle' | 'checking' | 'ok' | 'failed'
  const [verifyDetail, setVerifyDetail] = useState(null)
  const [copied, setCopied] = useState(null)

  function copy(label, text) {
    navigator.clipboard?.writeText(text)
      .then(() => { setCopied(label); setTimeout(() => setCopied(null), 1500) })
      .catch(() => alert('Clipboard copy failed — select the text manually.'))
  }

  async function runVerify() {
    setVerifyState('checking')
    setVerifyDetail(null)
    try {
      const r = await verify()
      // listSettings returns 200 only when shared-secret matches on both ends.
      setVerifyState('ok')
      setVerifyDetail(`${r.settings?.length || 0} settings read OK`)
    } catch (e) {
      setVerifyState('failed')
      setVerifyDetail(e.message)
    }
  }

  const cmdHighlights = `cd ${HIGHLIGHTS_PATH}\nnpx wrangler secret put HIGHLIGHTS_SHARED_SECRET\n# paste the secret above and press Enter`
  const cmdVader = `cd ${VADER_PATH}\nnpx wrangler pages secret put HIGHLIGHTS_SHARED_SECRET --project-name=tbtv-vader\n# paste the SAME value and press Enter`

  return (
    <div style={{
      marginTop: 12,
      border: '1px solid #333',
      borderRadius: 6,
      background: 'rgba(168, 85, 247, 0.04)',
    }}>
      <button
        onClick={() => setExpanded(v => !v)}
        style={{
          width: '100%', textAlign: 'left',
          padding: '10px 12px', background: 'transparent', border: 'none',
          color: 'inherit', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
        <span style={{ color: '#a78bfa' }}><KeyRound size={16} /></span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>Shared-secret rotation</div>
          <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>
            Generate and install a new <code>HIGHLIGHTS_SHARED_SECRET</code> on both workers. Brief ~30s window of 403s during install — admin UI recovers when both workers see the new value.
          </div>
        </div>
        {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </button>

      {expanded && (
        <div style={{ padding: '0 12px 12px 12px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Step 1: generate */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
              Step 1 — Generate a new value
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button
                onClick={() => { setSecret(genSecret()); setVerifyState('idle'); setVerifyDetail(null) }}
                style={{ padding: '6px 12px', background: '#3b82f6', border: 'none', borderRadius: 4, color: 'white', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <RefreshCw size={12} /> {secret ? 'Generate another' : 'Generate'}
              </button>
              {secret && (
                <span style={{ fontSize: 11, color: '#aaa' }}>
                  64-char hex · generated locally · never leaves this browser until you paste it into wrangler
                </span>
              )}
            </div>

            {secret && (
              <div style={{ marginTop: 8, display: 'flex', gap: 6, alignItems: 'stretch' }}>
                <code style={{
                  flex: 1, padding: '8px 10px', background: '#000',
                  border: '1px solid #333', borderRadius: 4, fontSize: 11,
                  fontFamily: 'ui-monospace, monospace', userSelect: 'all', wordBreak: 'break-all',
                }}>{secret}</code>
                <button onClick={() => copy('secret', secret)}
                  style={{ padding: '0 12px', background: '#222', border: '1px solid #333', borderRadius: 4, color: 'inherit', cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <Copy size={12} /> {copied === 'secret' ? 'Copied!' : 'Copy'}
                </button>
              </div>
            )}
          </div>

          {/* Steps 2 + 3: install commands */}
          {secret && (
            <>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                  Step 2 — Install on the highlights worker (Workers)
                </div>
                <CommandBlock cmd={cmdHighlights} copied={copied === 'cmd1'} onCopy={() => copy('cmd1', cmdHighlights)} />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                  Step 3 — Install on vader (Pages)
                </div>
                <CommandBlock cmd={cmdVader} copied={copied === 'cmd2'} onCopy={() => copy('cmd2', cmdVader)} />
                <div style={{ fontSize: 11, color: '#888', marginTop: 6, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                  <AlertTriangle size={12} style={{ marginTop: 2, flexShrink: 0, color: '#f59e0b' }} />
                  <span>Run steps 2 and 3 back-to-back. Between the two installs, admin API calls return 403 while the workers' secrets are out of sync.</span>
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                  Step 4 — Verify both workers agree
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button onClick={runVerify} disabled={verifyState === 'checking'}
                    style={{ padding: '6px 12px', background: '#22c55e', border: 'none', borderRadius: 4, color: 'white', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {verifyState === 'checking' ? <Loader2 size={12} className="spin" /> : <Check size={12} />}
                    Verify chain
                  </button>
                  {verifyState === 'ok' && (
                    <span style={{ fontSize: 12, color: '#22c55e' }}>
                      ✓ Verified — admin chain healthy ({verifyDetail})
                    </span>
                  )}
                  {verifyState === 'failed' && (
                    <span style={{ fontSize: 12, color: '#dc2626' }}>
                      ✗ {verifyDetail || 'verify failed'}
                    </span>
                  )}
                  {verifyState === 'idle' && (
                    <span style={{ fontSize: 12, color: '#888' }}>
                      hits <code>GET /settings</code>; 200 means both workers hold the new value
                    </span>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

function CommandBlock({ cmd, copied, onCopy }) {
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
      <pre style={{
        flex: 1, padding: '8px 10px', background: '#000', border: '1px solid #333',
        borderRadius: 4, fontSize: 11, fontFamily: 'ui-monospace, monospace',
        whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0, userSelect: 'all',
      }}>{cmd}</pre>
      <button onClick={onCopy}
        style={{ padding: '0 12px', background: '#222', border: '1px solid #333', borderRadius: 4, color: 'inherit', cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <Copy size={12} /> {copied ? 'Copied!' : 'Copy'}
      </button>
    </div>
  )
}

// Numeric setting for the daily Robots-job cap. Sentinel 0 = no cap.
// Validates 0 ≤ value ≤ 10000 client-side.
// Step 19: tri-state selector for the per-query Claude rewriter mode.
// Segmented control: off / opt_in / always_on. Active mode highlighted.
function RewriteModeSelector({ setting, busy, onSave }) {
  const current = setting.value || 'opt_in'
  const MODES = [
    { k: 'off',       label: 'Off',        color: '#666',     hint: 'Smart expand disabled. Editor button is ignored.' },
    { k: 'opt_in',    label: 'Opt-in',     color: '#3b82f6',  hint: 'Editor clicks ✨ Smart expand per query (default).' },
    { k: 'always_on', label: 'Always-on',  color: '#c4b5fd',  hint: 'Every search auto-runs Claude. Latency +600ms. ~$0.0008/search.' },
  ]
  return (
    <div style={{
      marginTop: 10, padding: 12, border: '1px solid #2a2a2a', borderRadius: 6,
      background: 'rgba(167,139,250,0.04)', display: 'flex', gap: 12, alignItems: 'flex-start',
    }}>
      <div style={{ marginTop: 4, color: '#c4b5fd' }}><Sparkles size={16} /></div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
          Claude query rewriter (Smart expand)
        </div>
        <div style={{ fontSize: 11, color: '#aaa', marginBottom: 8, lineHeight: 1.5 }}>
          Claude rewrites the typed query into 6–10 announcer phrasings + 3–5 STT-mishearing aliases before searching. Cached in KV for 30 days.
          Per-query cost: ~$0.0008 (Haiku 4.5). 100k searches/month at <strong>always_on</strong> with 40% cache hit ≈ $5/month.
        </div>
        <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
          {MODES.map(m => {
            const active = current === m.k
            return (
              <button key={m.k} onClick={() => onSave(m.k)} disabled={busy || active}
                title={m.hint}
                style={{
                  padding: '6px 12px', borderRadius: 999,
                  background: active ? m.color : 'transparent',
                  border: '1px solid ' + (active ? m.color : '#333'),
                  color: active ? '#000' : m.color,
                  cursor: active || busy ? 'default' : 'pointer',
                  fontSize: 12, fontWeight: active ? 700 : 500,
                }}>
                {m.label}
              </button>
            )
          })}
          {busy && <Loader2 size={12} className="spin" style={{ alignSelf: 'center', color: '#888' }} />}
        </div>
        <div style={{ fontSize: 10, color: '#666', fontFamily: 'monospace' }}>
          {setting.key} · current {current} · last changed {setting.updated_at || 'never'}
          {setting.updated_by ? ` by ${setting.updated_by}` : ''}
        </div>
      </div>
    </div>
  )
}

// Step 25 follow-up: cap on how many cues the LLM Find Key Moments
// detector may cite per moment. tight = 1..3, normal = 1..5 (default),
// loose = 1..10. The prompt has a {{MAX_CUE_SPAN}} placeholder
// substituted at runtime; server also enforces the cap on the returned
// cue range so the LLM can't blow past it.
function CueSpanModeSelector({ setting, busy, onSave }) {
  const current = setting.value || 'normal'
  const MODES = [
    { k: 'tight',  label: 'Tight',  color: '#3b82f6', hint: 'LLM cites at most 3 cues per moment. Clip bodies ~10–20s. Best when editor wants short, surgical highlights.' },
    { k: 'normal', label: 'Normal', color: '#22c55e', hint: 'LLM cites 1–5 cues per moment (default). Clip bodies ~15–40s. Balanced lead-up + payoff + reaction.' },
    { k: 'loose',  label: 'Loose',  color: '#c4b5fd', hint: 'LLM cites up to 10 cues per moment. Clip bodies ~30–80s. Cinematic context; longer per-clip duration.' },
  ]
  return (
    <div style={{
      marginTop: 10, padding: 12, border: '1px solid #2a2a2a', borderRadius: 6,
      background: 'rgba(34,197,94,0.04)', display: 'flex', gap: 12, alignItems: 'flex-start',
    }}>
      <div style={{ marginTop: 4, color: '#22c55e' }}><Sparkles size={16} /></div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
          TBTV-AI LLM — Cue span per moment
        </div>
        <div style={{ fontSize: 11, color: '#aaa', marginBottom: 8, lineHeight: 1.5 }}>
          Controls how many consecutive transcript cues the LLM may cite as one moment. Narrower spans = shorter, more discrete clips. Wider spans = more context per clip. Affects future LLM runs only; existing moments stay as-is.
          Per-reel <em>clip body</em> selector in the Reel Builder can further tighten on a per-reel basis.
        </div>
        <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
          {MODES.map(m => {
            const active = current === m.k
            return (
              <button key={m.k} onClick={() => onSave(m.k)} disabled={busy || active}
                title={m.hint}
                style={{
                  padding: '6px 12px', borderRadius: 999,
                  background: active ? m.color : 'transparent',
                  border: '1px solid ' + (active ? m.color : '#333'),
                  color: active ? '#000' : m.color,
                  cursor: active || busy ? 'default' : 'pointer',
                  fontSize: 12, fontWeight: active ? 700 : 500,
                }}>
                {m.label}
              </button>
            )
          })}
          {busy && <Loader2 size={12} className="spin" style={{ alignSelf: 'center', color: '#888' }} />}
        </div>
        <div style={{ fontSize: 10, color: '#666', fontFamily: 'monospace' }}>
          {setting.key} · current {current} · last changed {setting.updated_at || 'never'}
          {setting.updated_by ? ` by ${setting.updated_by}` : ''}
        </div>
      </div>
    </div>
  )
}

// Step 20: auto-apply confidence threshold for Claude alias scanner.
// Clusters at or above this confidence flip to active without editor review;
// below the threshold they queue in the AliasesEditor Pending tab.
function AliasThresholdInput({ setting, busy, onSave }) {
  const [draft, setDraft] = useState(setting.value)
  useEffect(() => { setDraft(setting.value) }, [setting.value])

  const current = parseFloat(setting.value)
  const draftNum = parseFloat(draft)
  const dirty = !Number.isNaN(draftNum) && Math.abs(draftNum - current) > 1e-6
  const valid = Number.isFinite(draftNum) && draftNum >= 0 && draftNum <= 1

  return (
    <div style={{
      marginTop: 10,
      padding: 12,
      border: '1px solid #2a2a2a',
      borderRadius: 6,
      background: 'rgba(167,139,250,0.04)',
      display: 'flex',
      gap: 12,
      alignItems: 'flex-start',
    }}>
      <div style={{ marginTop: 4, color: '#c4b5fd' }}><Bot size={16} /></div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
          Claude alias auto-apply threshold
        </div>
        <div style={{ fontSize: 11, color: '#aaa', marginBottom: 8, lineHeight: 1.5 }}>
          Confidence floor for Claude-proposed alias clusters to bypass review.
          At <strong>0.85</strong> (default) about 60% of proposals auto-apply;
          the rest queue in the Pending review tab of the AliasesEditor. Raise
          this if Claude's auto-applied aliases produce false-positive matches
          in search; lower it to reduce review workload.
        </div>
        <div style={{ fontSize: 10, color: '#666', fontFamily: 'monospace' }}>
          {setting.key} · current {Number.isFinite(current) ? current.toFixed(2) : '—'} · last changed {setting.updated_at || 'never'}
          {setting.updated_by ? ` by ${setting.updated_by}` : ''}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
        <input
          type="number"
          step={0.05}
          min={0}
          max={1}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          style={{
            width: 90, padding: '6px 10px', background: '#000',
            border: '1px solid ' + (valid ? '#333' : 'crimson'),
            borderRadius: 4, color: 'inherit', textAlign: 'right',
          }}
        />
        <button
          onClick={() => onSave(draftNum)}
          disabled={busy || !dirty || !valid}
          style={{
            padding: '4px 12px',
            background: dirty && valid ? '#3b82f6' : 'transparent',
            border: '1px solid ' + (dirty && valid ? '#3b82f6' : '#333'),
            borderRadius: 4,
            color: dirty && valid ? 'white' : '#666',
            cursor: dirty && valid ? 'pointer' : 'default',
            fontSize: 12,
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>
          {busy ? <Loader2 size={11} className="spin" /> : 'Save'}
        </button>
      </div>
    </div>
  )
}

function DailyCapInput({ setting, busy, onSave }) {
  const [draft, setDraft] = useState(setting.value)
  useEffect(() => { setDraft(setting.value) }, [setting.value])

  const current = parseInt(setting.value, 10) || 0
  const draftNum = parseInt(draft, 10) || 0
  const dirty = String(draftNum) !== String(current)
  const valid = draftNum >= 0 && draftNum <= 10000

  return (
    <div style={{
      marginTop: 10,
      padding: 12,
      border: '1px solid #444',
      borderRadius: 6,
      background: 'rgba(248, 113, 113, 0.04)',
      display: 'flex',
      gap: 12,
      alignItems: 'flex-start',
    }}>
      <div style={{ marginTop: 4, color: '#fca5a5' }}><Shield size={16} /></div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <div style={{ fontWeight: 600, fontSize: 13 }}>Daily Robots job cap</div>
          <span style={{
            fontSize: 11, padding: '1px 8px', borderRadius: 10,
            background: '#3a1414', color: '#fca5a5',
          }}>Security backstop</span>
        </div>
        <div style={{ fontSize: 11, color: '#aaa', marginBottom: 8, lineHeight: 1.5 }}>
          Maximum number of Mux Robots jobs that may be queued per UTC day,
          summed across all types and trigger paths. Blocks runaway spend even
          when both toggles are ON. Set <code>0</code> to disable the cap entirely
          (not recommended). Default <strong>200</strong>.
        </div>
        <div style={{ fontSize: 10, color: '#666', fontFamily: 'monospace' }}>
          {setting.key} · current {current} · last changed {setting.updated_at || 'never'}
          {setting.updated_by ? ` by ${setting.updated_by}` : ''}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
        <input
          type="number"
          min={0}
          max={10000}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          style={{
            width: 90, padding: '6px 10px', background: '#000',
            border: '1px solid ' + (valid ? '#333' : 'crimson'),
            borderRadius: 4, color: 'inherit', textAlign: 'right',
          }}
        />
        <button
          onClick={() => onSave(draftNum)}
          disabled={busy || !dirty || !valid}
          style={{
            padding: '4px 12px',
            background: dirty && valid ? '#3b82f6' : 'transparent',
            border: '1px solid ' + (dirty && valid ? '#3b82f6' : '#333'),
            borderRadius: 4,
            color: dirty && valid ? 'white' : '#666',
            cursor: dirty && valid ? 'pointer' : 'default',
            fontSize: 12,
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>
          {busy ? <Loader2 size={11} className="spin" /> : 'Save'}
        </button>
      </div>
    </div>
  )
}

// Step 27 UI option A: TBTV-AI Eval panel.
// Surfaces the latest grader run + per-video / per-type breakdown +
// a "Run eval" button so editorial doesn't drop to the CLI. Reads from
// the `eval_runs` history + the `approved_moments` / `dropped_moments`
// coverage tallies. Empty-state copy tells the editor what to do.
function EvalPanel() {
  const api = useHighlightsApi()
  const [latest, setLatest] = useState(null)
  const [coverage, setCoverage] = useState(null)
  const [loading, setLoading] = useState(true)
  const [running, setRunning] = useState(false)
  const [err, setErr] = useState(null)
  const [expanded, setExpanded] = useState(false)
  // Step 27 follow-up: review modal for inspecting/undoing labels before eval.
  const [reviewOpen, setReviewOpen] = useState(false)

  const refresh = useCallback(() => {
    setLoading(true)
    setErr(null)
    api.evalLatest()
      .then(r => { setLatest(r?.latest || null); setCoverage(r?.coverage || null) })
      .catch(e => setErr(e?.message || String(e)))
      .finally(() => setLoading(false))
  }, [api])

  useEffect(() => { refresh() }, [refresh])

  async function runNow() {
    if (running) return
    setRunning(true)
    setErr(null)
    try {
      const label = `ui-${new Date().toISOString().slice(0, 19).replace('T', '-')}`
      await api.runEval({ run_label: label })
      refresh()
    } catch (e) {
      setErr(e?.message || String(e))
    } finally {
      setRunning(false)
    }
  }

  return (
    <div style={{
      border: '1px solid #1e293b', borderRadius: 8, padding: 14,
      marginBottom: 16, background: '#0d1622',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <Activity size={14} style={{ color: '#7dd3fc' }} />
        <div style={{ fontSize: 13, fontWeight: 600, color: '#bfdbfe', flex: 1 }}>
          TBTV-AI Eval
        </div>
        <button onClick={() => setReviewOpen(true)}
          disabled={!coverage?.approved_total && !coverage?.dropped_total}
          title={coverage?.approved_total || coverage?.dropped_total
            ? 'Review every approval + rejection across the catalog. Undo any wrong labels before running eval.'
            : 'No labels yet — start with ✓ Approve / ❌ Reject in Caption Library or Key Moments.'}
          style={{ padding: '3px 10px', fontSize: 11, background: 'transparent',
            border: '1px solid #14532d', borderRadius: 4, color: '#4ade80',
            cursor: (coverage?.approved_total || coverage?.dropped_total) ? 'pointer' : 'not-allowed',
            opacity: (coverage?.approved_total || coverage?.dropped_total) ? 1 : 0.4,
            display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <BookOpen size={11} /> Review labels
        </button>
        <button onClick={refresh} disabled={loading}
          title="Refresh — re-reads the latest eval_runs row without firing a new grade"
          style={{ padding: '3px 8px', fontSize: 11, background: 'transparent',
            border: '1px solid #333', borderRadius: 4, color: '#888',
            cursor: loading ? 'default' : 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <RefreshCw size={11} className={loading ? 'spin' : ''} /> Refresh
        </button>
        <button onClick={runNow} disabled={running || !coverage?.approved_total}
          title={coverage?.approved_total
            ? 'Run grader server-side; persists to eval_runs'
            : '✓ Approve moments in the admin UI first to build the gold set'}
          style={{ padding: '3px 10px', fontSize: 11,
            background: running ? '#222' : (coverage?.approved_total ? '#1e40af' : '#222'),
            border: '1px solid ' + (coverage?.approved_total ? '#3b82f6' : '#333'),
            borderRadius: 4, color: coverage?.approved_total ? '#dbeafe' : '#555',
            cursor: (running || !coverage?.approved_total) ? 'not-allowed' : 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {running ? <Loader2 size={11} className="spin" /> : <PlayIcon size={11} />}
          Run eval
        </button>
      </div>

      {err && (
        <div style={{ fontSize: 11, color: '#fca5a5', marginBottom: 8 }}>
          {err}
        </div>
      )}

      {/* Coverage strip — always visible. */}
      <div style={{ display: 'flex', gap: 14, fontSize: 11, color: '#aaa', flexWrap: 'wrap' }}>
        <div>
          <span style={{ color: '#4ade80' }}>✓ {coverage?.approved_total ?? '—'}</span> approved
          {coverage?.approved_videos != null && coverage.approved_videos > 0 &&
            <span style={{ color: '#666' }}> across {coverage.approved_videos} video{coverage.approved_videos === 1 ? '' : 's'}</span>}
        </div>
        <div>
          <span style={{ color: '#fca5a5' }}>✗ {coverage?.dropped_total ?? '—'}</span> rejected
          {coverage?.dropped_videos != null && coverage.dropped_videos > 0 &&
            <span style={{ color: '#666' }}> across {coverage.dropped_videos} video{coverage.dropped_videos === 1 ? '' : 's'}</span>}
        </div>
      </div>

      {/* Empty state */}
      {!loading && !latest && !err && (
        <div style={{ marginTop: 10, fontSize: 11, color: '#888', lineHeight: 1.55 }}>
          {coverage?.approved_total
            ? <>Have approvals but no eval yet — click <strong>Run eval</strong> above to grade the current TBTV-AI output against your gold set.</>
            : <>No gold labels yet. Click <strong>✓ Approve</strong> on real reel-worthy moments in Caption Library, Key Moments Explorer, ClipPreview, or Reel Editor. After ~30 approvals, run the grader.</>}
        </div>
      )}

      {/* Latest run summary */}
      {latest && (
        <>
          <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            <ScoreBox label="Precision" value={latest.precision_pct} hint={`${latest.true_positives} TP / ${latest.true_positives + latest.false_positives} emitted`} />
            <ScoreBox label="Recall"    value={latest.recall_pct}    hint={`${latest.true_positives} TP / ${latest.true_positives + latest.false_negatives} gold`} />
            <ScoreBox label="F1"        value={latest.f1}            hint={`gold=${latest.gold_count}  km=${latest.km_count}`} />
          </div>
          <div style={{ marginTop: 8, fontSize: 10, color: '#666', display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            <span>Last run: {fmtAgo(latest.ran_at)}</span>
            <span>·</span>
            <span>Label: <span style={{ color: '#aaa' }}>{latest.run_label}</span></span>
            <span>·</span>
            <span>Source: <span style={{ color: '#aaa' }}>{latest.gold_set}</span></span>
            <button onClick={() => setExpanded(e => !e)}
              style={{ background: 'transparent', border: 'none', color: '#7dd3fc', cursor: 'pointer', padding: 0, fontSize: 10, display: 'inline-flex', alignItems: 'center', gap: 2 }}>
              {expanded ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
              {expanded ? 'Hide breakdown' : 'Show breakdown'}
            </button>
          </div>

          {expanded && (
            <EvalBreakdown detail={latest.detail_json} />
          )}
        </>
      )}

      {reviewOpen && (
        <ReviewLabelsModal onClose={() => { setReviewOpen(false); refresh() }} />
      )}
    </div>
  )
}

function ScoreBox({ label, value, hint }) {
  const pct = value == null ? '—' : `${(value * 100).toFixed(1)}%`
  return (
    <div style={{ background: '#020817', border: '1px solid #1e293b',
      borderRadius: 4, padding: '8px 10px' }}>
      <div style={{ fontSize: 9, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 600, color: '#bfdbfe', fontFamily: 'monospace', marginTop: 2 }}>{pct}</div>
      <div style={{ fontSize: 10, color: '#666', marginTop: 2 }}>{hint}</div>
    </div>
  )
}

function EvalBreakdown({ detail }) {
  let parsed
  try { parsed = typeof detail === 'string' ? JSON.parse(detail) : detail }
  catch { return null }
  if (!parsed) return null
  return (
    <div style={{ marginTop: 10, fontSize: 11 }}>
      {Array.isArray(parsed.per_type) && parsed.per_type.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Per moment_type</div>
          <table style={{ width: '100%', fontSize: 11, color: '#aaa', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ color: '#666' }}>
                <th style={{ textAlign: 'left', padding: '2px 6px', borderBottom: '1px solid #1e293b' }}>type</th>
                <th style={{ textAlign: 'right', padding: '2px 6px', borderBottom: '1px solid #1e293b' }}>P</th>
                <th style={{ textAlign: 'right', padding: '2px 6px', borderBottom: '1px solid #1e293b' }}>R</th>
                <th style={{ textAlign: 'right', padding: '2px 6px', borderBottom: '1px solid #1e293b' }}>TP</th>
                <th style={{ textAlign: 'right', padding: '2px 6px', borderBottom: '1px solid #1e293b' }}>FP</th>
                <th style={{ textAlign: 'right', padding: '2px 6px', borderBottom: '1px solid #1e293b' }}>FN</th>
              </tr>
            </thead>
            <tbody>
              {parsed.per_type.map(t => (
                <tr key={t.moment_type}>
                  <td style={{ padding: '2px 6px' }}>{t.moment_type}</td>
                  <td style={{ padding: '2px 6px', textAlign: 'right', fontFamily: 'monospace' }}>{pctStr(t.precision)}</td>
                  <td style={{ padding: '2px 6px', textAlign: 'right', fontFamily: 'monospace' }}>{pctStr(t.recall)}</td>
                  <td style={{ padding: '2px 6px', textAlign: 'right', fontFamily: 'monospace' }}>{t.tp}</td>
                  <td style={{ padding: '2px 6px', textAlign: 'right', fontFamily: 'monospace' }}>{t.fp}</td>
                  <td style={{ padding: '2px 6px', textAlign: 'right', fontFamily: 'monospace' }}>{t.fn}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {Array.isArray(parsed.per_video) && parsed.per_video.length > 0 && (
        <div>
          <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Per video</div>
          <table style={{ width: '100%', fontSize: 11, color: '#aaa', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ color: '#666' }}>
                <th style={{ textAlign: 'left', padding: '2px 6px', borderBottom: '1px solid #1e293b' }}>tbtv_id</th>
                <th style={{ textAlign: 'right', padding: '2px 6px', borderBottom: '1px solid #1e293b' }}>P</th>
                <th style={{ textAlign: 'right', padding: '2px 6px', borderBottom: '1px solid #1e293b' }}>R</th>
                <th style={{ textAlign: 'right', padding: '2px 6px', borderBottom: '1px solid #1e293b' }}>TP</th>
                <th style={{ textAlign: 'right', padding: '2px 6px', borderBottom: '1px solid #1e293b' }}>FP</th>
                <th style={{ textAlign: 'right', padding: '2px 6px', borderBottom: '1px solid #1e293b' }}>FN</th>
              </tr>
            </thead>
            <tbody>
              {parsed.per_video.map(v => (
                <tr key={v.tbtv_id}>
                  <td style={{ padding: '2px 6px', fontFamily: 'monospace', fontSize: 10 }}>{v.tbtv_id}</td>
                  <td style={{ padding: '2px 6px', textAlign: 'right', fontFamily: 'monospace' }}>{pctStr(v.precision)}</td>
                  <td style={{ padding: '2px 6px', textAlign: 'right', fontFamily: 'monospace' }}>{pctStr(v.recall)}</td>
                  <td style={{ padding: '2px 6px', textAlign: 'right', fontFamily: 'monospace' }}>{v.tp}</td>
                  <td style={{ padding: '2px 6px', textAlign: 'right', fontFamily: 'monospace' }}>{v.fp}</td>
                  <td style={{ padding: '2px 6px', textAlign: 'right', fontFamily: 'monospace' }}>{v.fn}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// Step 29: RAG exemplar panel. Calls /admin/rag/preview to show the
// editor exactly what the LLM is going to see — count of drops + approves
// + the formatted block. Editorial trust signal: "your rejections are
// actually compounding into the prompt."
function RagPanel() {
  const api = useHighlightsApi()
  const [preview, setPreview] = useState(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)
  const [expanded, setExpanded] = useState(false)

  const refresh = useCallback(() => {
    setLoading(true)
    setErr(null)
    api.ragPreview()
      .then(r => setPreview(r))
      .catch(e => setErr(e?.message || String(e)))
      .finally(() => setLoading(false))
  }, [api])

  useEffect(() => { refresh() }, [refresh])

  return (
    <div style={{
      border: '1px solid #1e293b', borderRadius: 8, padding: 14,
      marginBottom: 16, background: '#0d1622',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <Sparkles size={14} style={{ color: '#fbbf24' }} />
        <div style={{ fontSize: 13, fontWeight: 600, color: '#fde68a', flex: 1 }}>
          RAG exemplars — editorial decisions compounding into the prompt
        </div>
        <button onClick={refresh} disabled={loading}
          style={{
            background: 'transparent', border: '1px solid #334155',
            color: '#cbd5e1', padding: '4px 8px', fontSize: 11, borderRadius: 4,
            cursor: loading ? 'wait' : 'pointer',
          }}
          title="Re-fetch the current exemplar set">
          {loading ? <Loader2 size={11} className="spin" /> : <RefreshCw size={11} />}
        </button>
      </div>
      {err && (
        <div style={{ fontSize: 11, color: '#f87171', marginBottom: 8 }}>{err}</div>
      )}
      {!err && preview && (
        <>
          {!preview.enabled && (
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 6 }}>
              RAG injection is OFF. Enable it via the <code>tbtv_ai_rag_exemplars_enabled</code> setting to start feeding editorial decisions into the LLM.
            </div>
          )}
          {preview.enabled && preview.drop_count === 0 && preview.approve_count === 0 && (
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 6 }}>
              No editorial labels yet. Reject (with reason <code>hallucinated</code> or <code>recap</code>) or approve a few moments from KeyMomentsExplorer and they'll appear here.
            </div>
          )}
          {preview.enabled && (preview.drop_count > 0 || preview.approve_count > 0) && (
            <>
              <div style={{ fontSize: 12, color: '#cbd5e1', marginBottom: 6 }}>
                Next LLM call will see <strong style={{ color: '#fca5a5' }}>{preview.drop_count} rejection{preview.drop_count === 1 ? '' : 's'}</strong> + <strong style={{ color: '#86efac' }}>{preview.approve_count} approval{preview.approve_count === 1 ? '' : 's'}</strong> from your recent editorial work on other videos.
              </div>
              <button onClick={() => setExpanded(v => !v)}
                style={{
                  background: 'transparent', border: '1px solid #334155',
                  color: '#cbd5e1', padding: '4px 8px', fontSize: 11, borderRadius: 4,
                  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
                }}>
                {expanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                {expanded ? 'Hide' : 'Show'} what the model sees
              </button>
              {expanded && (
                <pre style={{
                  marginTop: 8, padding: 10, background: '#020617',
                  border: '1px solid #1e293b', borderRadius: 4, fontSize: 10,
                  color: '#cbd5e1', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  maxHeight: 320, overflowY: 'auto',
                }}>{preview.formatted}</pre>
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}

// Step 26.13 UI: stuck + errored job recovery.
// Two tables. "🟡 Stuck in flight" lists jobs older than 3 minutes still
// in pending/processing. "❌ Recently errored" lists status='errored' or
// any sublayer-errored rows from the last 7 days. Editor can multi-select
// and bulk Unstick (sets status='errored' so shouldRunJob lets the next
// re-fire through) or bulk Re-run (calls robotsApply with force=true).
function JobRecoveryPanel() {
  const api = useHighlightsApi()
  const [stuck, setStuck] = useState([])
  const [errored, setErrored] = useState([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [stuckSel, setStuckSel] = useState(new Set())
  const [erroredSel, setErroredSel] = useState(new Set())
  const [expanded, setExpanded] = useState(true)

  const refresh = useCallback(async () => {
    setLoading(true)
    setErr(null)
    try {
      const [s, e] = await Promise.all([
        api.listStuckJobs({ min_age_minutes: 3 }),
        api.listErroredJobs({ days: 7 }),
      ])
      setStuck(s?.jobs || [])
      setErrored(e?.jobs || [])
      setStuckSel(new Set())
      setErroredSel(new Set())
    } catch (e) {
      setErr(e?.message || String(e))
    } finally {
      setLoading(false)
    }
  }, [api])

  useEffect(() => { refresh() }, [refresh])

  async function unstickSelected() {
    if (busy || stuckSel.size === 0) return
    if (!confirm(`Unstick ${stuckSel.size} job(s)? Marks each errored so re-fire is allowed.`)) return
    setBusy(true)
    try {
      await api.unstickJobs(Array.from(stuckSel))
      await refresh()
    } catch (e) {
      alert(`Unstick failed: ${e?.message || e}`)
    } finally { setBusy(false) }
  }

  async function rerunSelected() {
    if (busy || erroredSel.size === 0) return
    // Build the (job_type, tbtv_id) list. ONE HTTP request per pair so each
    // gets its own ctx.waitUntil budget on the worker side — batching N pairs
    // into one request runs them all under the same ~30s waitUntil cap and
    // most never start, creating new "stuck in flight" rows.
    const pairs = []
    const seen = new Set()
    for (const job of errored) {
      if (!erroredSel.has(job.job_id)) continue
      const key = `${job.job_type}|${job.tbtv_id}`
      if (seen.has(key)) continue
      seen.add(key)
      pairs.push({ jobType: job.job_type, tbtvId: job.tbtv_id })
    }
    if (pairs.length === 0) return
    if (!confirm(`Re-run ${pairs.length} job(s)? Each fires as a separate request to avoid the shared-waitUntil-budget bug.`)) return
    setBusy(true)
    let ok = 0
    let failed = 0
    try {
      // Serial loop; ~1-2s per request is fine for the recovery flow and
      // keeps the worker from queuing parallel triggers it can't finish.
      for (const { jobType, tbtvId } of pairs) {
        try {
          await api.robotsApply({ jobType, tbtvIds: [tbtvId], force: true })
          ok += 1
        } catch (e) {
          failed += 1
          console.error(`re-run failed for ${jobType}/${tbtvId}:`, e)
        }
      }
      if (failed > 0) {
        alert(`Re-run finished: ${ok} fired, ${failed} failed. Check console for details.`)
      }
      await refresh()
    } finally { setBusy(false) }
  }

  function toggleSel(set, setSet, id) {
    const next = new Set(set)
    if (next.has(id)) next.delete(id); else next.add(id)
    setSet(next)
  }
  function selectAll(jobs, setSet) {
    setSet(new Set(jobs.map(j => j.job_id)))
  }

  const totalShown = stuck.length + errored.length

  return (
    <div style={{
      border: '1px solid #1e293b', borderRadius: 8, padding: 14,
      marginBottom: 16, background: '#0d1622',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <Shield size={14} style={{ color: '#fb923c' }} />
        <div style={{ fontSize: 13, fontWeight: 600, color: '#fed7aa', flex: 1, cursor: 'pointer' }}
          onClick={() => setExpanded(v => !v)}>
          {expanded ? <ChevronDown size={11} style={{ marginRight: 4, display: 'inline' }} /> : <ChevronRight size={11} style={{ marginRight: 4, display: 'inline' }} />}
          Job recovery
          {totalShown > 0 && (
            <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 400, color: '#94a3b8' }}>
              ({stuck.length} stuck, {errored.length} errored)
            </span>
          )}
        </div>
        <button onClick={refresh} disabled={loading || busy}
          style={{
            background: 'transparent', border: '1px solid #334155',
            color: '#cbd5e1', padding: '4px 8px', fontSize: 11, borderRadius: 4,
            cursor: loading || busy ? 'wait' : 'pointer',
          }}
          title="Re-fetch the recovery list">
          {loading ? <Loader2 size={11} className="spin" /> : <RefreshCw size={11} />}
        </button>
      </div>
      {err && (
        <div style={{ fontSize: 11, color: '#f87171', marginBottom: 8 }}>{err}</div>
      )}
      {expanded && !err && !loading && totalShown === 0 && (
        <div style={{ fontSize: 12, color: '#86efac' }}>✓ No stuck or errored jobs in the last 7 days.</div>
      )}
      {expanded && stuck.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 12, color: '#fcd34d', fontWeight: 600 }}>🟡 Stuck in flight (≥ 3 min old)</span>
            <button onClick={() => selectAll(stuck, setStuckSel)}
              style={{ background: 'transparent', border: '1px solid #334155', color: '#94a3b8', padding: '2px 6px', fontSize: 10, borderRadius: 3, cursor: 'pointer' }}>
              Select all
            </button>
            <div style={{ flex: 1 }} />
            <button onClick={unstickSelected} disabled={busy || stuckSel.size === 0}
              style={{
                background: stuckSel.size === 0 ? '#1f2937' : '#dc2626',
                border: 'none', color: '#fff', padding: '4px 10px', fontSize: 11,
                borderRadius: 4, cursor: stuckSel.size === 0 || busy ? 'not-allowed' : 'pointer',
                opacity: stuckSel.size === 0 ? 0.5 : 1,
              }}>
              Unstick {stuckSel.size > 0 ? stuckSel.size : ''}
            </button>
          </div>
          <JobsTable jobs={stuck} selected={stuckSel} onToggle={(id) => toggleSel(stuckSel, setStuckSel, id)} />
        </div>
      )}
      {expanded && errored.length > 0 && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 12, color: '#fca5a5', fontWeight: 600 }}>❌ Recently errored (last 7 days)</span>
            <button onClick={() => selectAll(errored, setErroredSel)}
              style={{ background: 'transparent', border: '1px solid #334155', color: '#94a3b8', padding: '2px 6px', fontSize: 10, borderRadius: 3, cursor: 'pointer' }}>
              Select all
            </button>
            <div style={{ flex: 1 }} />
            <button onClick={rerunSelected} disabled={busy || erroredSel.size === 0}
              style={{
                background: erroredSel.size === 0 ? '#1f2937' : '#2563eb',
                border: 'none', color: '#fff', padding: '4px 10px', fontSize: 11,
                borderRadius: 4, cursor: erroredSel.size === 0 || busy ? 'not-allowed' : 'pointer',
                opacity: erroredSel.size === 0 ? 0.5 : 1,
              }}>
              Re-run {erroredSel.size > 0 ? erroredSel.size : ''}
            </button>
          </div>
          <JobsTable jobs={errored} selected={erroredSel} onToggle={(id) => toggleSel(erroredSel, setErroredSel, id)} showError />
        </div>
      )}
    </div>
  )
}

function JobsTable({ jobs, selected, onToggle, showError }) {
  return (
    <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid #1e293b', borderRadius: 4 }}>
      <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: '#020617', color: '#94a3b8' }}>
            <th style={{ padding: '4px 6px', textAlign: 'left', width: 24 }}></th>
            <th style={{ padding: '4px 6px', textAlign: 'left' }}>tbtv_id</th>
            <th style={{ padding: '4px 6px', textAlign: 'left' }}>job_type</th>
            <th style={{ padding: '4px 6px', textAlign: 'left' }}>status</th>
            <th style={{ padding: '4px 6px', textAlign: 'left' }}>age</th>
            {showError && <th style={{ padding: '4px 6px', textAlign: 'left' }}>error</th>}
          </tr>
        </thead>
        <tbody>
          {jobs.map(j => (
            <tr key={j.job_id} style={{ borderTop: '1px solid #1e293b' }}>
              <td style={{ padding: '4px 6px' }}>
                <input type="checkbox" checked={selected.has(j.job_id)} onChange={() => onToggle(j.job_id)} />
              </td>
              <td style={{ padding: '4px 6px', fontFamily: 'monospace', fontSize: 10 }}>{j.tbtv_id}</td>
              <td style={{ padding: '4px 6px', color: '#cbd5e1' }}>{j.job_type}</td>
              <td style={{ padding: '4px 6px', color: '#cbd5e1' }}>
                {j.status}
                {j.llm_status && <span style={{ color: '#94a3b8' }}> · llm:{j.llm_status}</span>}
                {j.kw_status && <span style={{ color: '#94a3b8' }}> · kw:{j.kw_status}</span>}
              </td>
              <td style={{ padding: '4px 6px', color: '#94a3b8', fontFamily: 'monospace' }}>{fmtAgo(j.requested_at)}</td>
              {showError && (
                <td style={{ padding: '4px 6px', color: '#fca5a5', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={j.error_message}>
                  {j.error_message || '—'}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// Step 28A Phase 2b: entity-resolver backfill driver.
// Lists indexed videos with their current cue_entity_mentions coverage.
// Per-row "Resolve" button POSTs the backfill for that video only (one
// request per video so each gets its own ctx.waitUntil budget — same bug
// pattern we fixed in JobRecoveryPanel.rerunSelected).
function EntityBackfillPanel() {
  const api = useHighlightsApi()
  const [videos, setVideos] = useState([])
  const [filteredOut, setFilteredOut] = useState(0)
  const [filterNote, setFilterNote] = useState(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(new Set())  // tbtv_ids of in-flight backfills (parallel)
  const [bulkBusy, setBulkBusy] = useState(false)
  const [lastResult, setLastResult] = useState(new Map())  // tbtv_id → { kind, message }
  const [bulkSummary, setBulkSummary] = useState(null)
  const [expandedRows, setExpandedRows] = useState(new Set())
  const [mentionsByVideo, setMentionsByVideo] = useState(new Map())

  function toggleRow(tbtv_id) {
    setExpandedRows(prev => {
      const next = new Set(prev)
      if (next.has(tbtv_id)) next.delete(tbtv_id); else next.add(tbtv_id)
      return next
    })
    if (!mentionsByVideo.has(tbtv_id)) {
      api.listEntityMentions(tbtv_id)
        .then(r => setMentionsByVideo(prev => {
          const next = new Map(prev)
          next.set(tbtv_id, r?.players || [])
          return next
        }))
        .catch(e => console.warn('mentions fetch failed', e))
    }
  }
  const [err, setErr] = useState(null)
  const [expanded, setExpanded] = useState(true)
  const [showAll, setShowAll] = useState(false)

  const refresh = useCallback(async () => {
    setLoading(true); setErr(null)
    try {
      const r = await api.listEntityBackfill({ include_no_roster: showAll })
      setVideos(r?.videos || [])
      setFilteredOut(r?.filtered_out || 0)
      setFilterNote(r?.filter_note || null)
    } catch (e) {
      setErr(e?.message || String(e))
    } finally { setLoading(false) }
  }, [api, showAll])

  useEffect(() => { refresh() }, [refresh])

  function markBusy(tbtv_id, isBusy) {
    setBusy(prev => {
      const next = new Set(prev)
      if (isBusy) next.add(tbtv_id); else next.delete(tbtv_id)
      return next
    })
  }
  function setResult(tbtv_id, kind, message) {
    setLastResult(prev => {
      const next = new Map(prev)
      next.set(tbtv_id, { kind, message, at: Date.now() })
      return next
    })
  }

  function summarizeResult(r) {
    if (r?.skipped) {
      const why = r.skipped_reason === 'no_vs_pattern_in_title'
        ? 'no "X vs Y" pattern in title — can\'t scope to teams'
        : r.skipped_reason === 'team_names_not_in_rosters'
          ? `team names ${(r.team_hints || []).join(' / ')} not in any imported roster`
          : (r.skipped_reason || 'unknown')
      return { kind: 'skipped', message: `Skipped: ${why}` }
    }
    const m = r?.mentions_written ?? 0
    const sp = r?.spans_resolved ?? 0
    const un = r?.spans_unmatched ?? 0
    return { kind: 'ok', message: `${m} mentions · ${sp} spans matched · ${un} unmatched` }
  }

  async function resolveOne(tbtv_id) {
    if (busy.has(tbtv_id)) return  // already in flight; ignore re-clicks
    markBusy(tbtv_id, true); setErr(null)
    try {
      const r = await api.runEntityBackfill(tbtv_id)
      const { kind, message } = summarizeResult(r)
      setResult(tbtv_id, kind, message)
      await refresh()
    } catch (e) {
      setResult(tbtv_id, 'error', e?.message || String(e))
    } finally { markBusy(tbtv_id, false) }
  }

  async function resolveAllUnresolved() {
    const queue = videos.filter(v => (v.mention_count || 0) === 0)
    if (queue.length === 0) return
    if (!confirm(`Resolve mentions for ${queue.length} unresolved video(s)? Each runs serially.`)) return
    setBulkBusy(true); setErr(null); setBulkSummary(null)
    let resolved = 0, skipped = 0, errored = 0
    for (const v of queue) {
      try {
        markBusy(v.tbtv_id, true)
        const r = await api.runEntityBackfill(v.tbtv_id)
        const { kind, message } = summarizeResult(r)
        setResult(v.tbtv_id, kind, message)
        if (kind === 'skipped') skipped++; else resolved++
      } catch (e) {
        errored++
        setResult(v.tbtv_id, 'error', e?.message || String(e))
      } finally {
        markBusy(v.tbtv_id, false)
      }
    }
    setBulkSummary({ total: queue.length, resolved, skipped, errored })
    setBulkBusy(false)
    refresh()
  }

  const unresolvedCount = videos.filter(v => (v.mention_count || 0) === 0).length
  const totalMentions = videos.reduce((acc, v) => acc + (v.mention_count || 0), 0)

  return (
    <div style={{
      border: '1px solid #1e293b', borderRadius: 8, padding: 14,
      marginBottom: 16, background: '#0d1622',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <Activity size={14} style={{ color: '#a78bfa' }} />
        <div style={{ fontSize: 13, fontWeight: 600, color: '#ddd6fe', flex: 1, cursor: 'pointer' }}
          onClick={() => setExpanded(v => !v)}>
          {expanded ? <ChevronDown size={11} style={{ marginRight: 4, display: 'inline' }} /> : <ChevronRight size={11} style={{ marginRight: 4, display: 'inline' }} />}
          Entity resolution (Phase 2b)
          {videos.length > 0 && (
            <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 400, color: '#94a3b8' }}>
              ({videos.length} indexed · {totalMentions} mentions resolved · {unresolvedCount} unresolved)
            </span>
          )}
        </div>
        <button onClick={refresh} disabled={loading || busy}
          style={{
            background: 'transparent', border: '1px solid #334155',
            color: '#cbd5e1', padding: '4px 8px', fontSize: 11, borderRadius: 4,
            cursor: (loading || busy) ? 'wait' : 'pointer',
          }}>
          {loading ? <Loader2 size={11} className="spin" /> : <RefreshCw size={11} />}
        </button>
      </div>
      {err && <div style={{ fontSize: 11, color: '#f87171', marginBottom: 8 }}>{err}</div>}

      {expanded && !loading && (
        <>
          {/* Step 28A Phase 2c: roster-grounded prompts toggle. */}
          <RosterGroundingToggle />

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, fontSize: 11 }}>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#cbd5e1', cursor: 'pointer' }}>
              <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} />
              Show all indexed videos (incl. content without applicable rosters)
            </label>
            {filteredOut > 0 && !showAll && (
              <span style={{ color: '#94a3b8' }}>
                · {filteredOut} hidden (girls hockey, basketball, football, lacrosse, concerts, etc.)
              </span>
            )}
          </div>

          {videos.length > 0 && unresolvedCount > 0 && (
            <div style={{ marginBottom: 10 }}>
              <button onClick={resolveAllUnresolved} disabled={bulkBusy}
                style={{
                  background: '#7c3aed', border: 'none', color: '#fff',
                  padding: '6px 10px', fontSize: 11, borderRadius: 4,
                  cursor: bulkBusy ? 'wait' : 'pointer',
                }}>
                {bulkBusy ? <Loader2 size={11} className="spin" /> : <Activity size={11} />}
                {' '}Resolve all {unresolvedCount} unresolved (~5-15s each)
              </button>
              <span style={{ marginLeft: 8, fontSize: 10, color: '#888' }}>
                Each video is its own request so they don't share waitUntil budget.
              </span>
            </div>
          )}

          {bulkSummary && (
            <div style={{ marginBottom: 8, padding: '6px 10px', background: '#020617', border: '1px solid #1e293b', borderRadius: 4, fontSize: 11 }}>
              <strong style={{ color: '#dbe5f5' }}>Bulk run done:</strong>{' '}
              {bulkSummary.total} total ·{' '}
              <span style={{ color: '#86efac' }}>{bulkSummary.resolved} resolved</span> ·{' '}
              <span style={{ color: '#fbbf24' }}>{bulkSummary.skipped} skipped (no team scope)</span>
              {bulkSummary.errored > 0 && <> · <span style={{ color: '#f87171' }}>{bulkSummary.errored} errored</span></>}
            </div>
          )}

          {videos.length > 0 && (
            <div style={{ maxHeight: 360, overflowY: 'auto', border: '1px solid #1e293b', borderRadius: 4 }}>
              <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#020617', color: '#94a3b8' }}>
                    <th style={{ padding: '4px 6px', textAlign: 'left' }}>Title</th>
                    <th style={{ padding: '4px 6px', textAlign: 'right' }}>Cues</th>
                    <th style={{ padding: '4px 6px', textAlign: 'right' }}>w/ mentions</th>
                    <th style={{ padding: '4px 6px', textAlign: 'right' }}>Mentions</th>
                    <th style={{ padding: '4px 6px', textAlign: 'right' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {videos.map(v => {
                    const inflight = busy.has(v.tbtv_id)
                    const hasAny = (v.mention_count || 0) > 0
                    const result = lastResult.get(v.tbtv_id)
                    const isExpanded = expandedRows.has(v.tbtv_id)
                    const titleHasVs = v.title && /\s+(?:vs\.?|at|@|v\.)\s+/i.test(v.title)
                    const willLikelySkip = v.title && !titleHasVs && !hasAny
                    const mentionDetails = mentionsByVideo.get(v.tbtv_id)
                    return (
                      <Fragment key={v.tbtv_id}>
                        <tr style={{ borderTop: '1px solid #1e293b' }}>
                          <td style={{ padding: '4px 6px', color: '#dbe5f5', maxWidth: 360 }}
                            title={`${v.tbtv_id}\n${v.title || ''}`}>
                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
                              {hasAny && (
                                <button onClick={() => toggleRow(v.tbtv_id)}
                                  style={{ background: 'transparent', border: 'none', color: '#94a3b8', padding: 0, cursor: 'pointer' }}
                                  title={isExpanded ? 'Hide mentions detail' : 'Show mentions detail'}>
                                  {isExpanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                                </button>
                              )}
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {v.title || <em style={{ color: '#666' }}>(no title)</em>}
                              </span>
                              {willLikelySkip && (
                                <span style={{ fontSize: 9, color: '#fbbf24', background: 'rgba(251,191,36,0.1)', padding: '0 4px', borderRadius: 3, whiteSpace: 'nowrap' }}
                                  title="No 'vs/at/@/v.' pattern detected in title — resolver will skip">
                                  ⚠ no vs
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: 9, color: '#666', fontFamily: 'monospace' }}>{v.tbtv_id}</div>
                            {result && (
                              <div style={{
                                fontSize: 10, marginTop: 2,
                                color: result.kind === 'ok' ? '#86efac'
                                    : result.kind === 'skipped' ? '#fbbf24'
                                    : '#f87171',
                              }}>
                                {result.message}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '4px 6px', textAlign: 'right', fontFamily: 'monospace', color: '#cbd5e1' }}>{v.cue_count}</td>
                          <td style={{ padding: '4px 6px', textAlign: 'right', fontFamily: 'monospace', color: hasAny ? '#86efac' : '#666' }}>{v.cues_with_mentions || 0}</td>
                          <td style={{ padding: '4px 6px', textAlign: 'right', fontFamily: 'monospace', color: hasAny ? '#7dd3fc' : '#666' }}>{v.mention_count || 0}</td>
                          <td style={{ padding: '4px 6px', textAlign: 'right' }}>
                            <button onClick={() => resolveOne(v.tbtv_id)} disabled={inflight}
                              style={{
                                background: hasAny ? 'transparent' : '#2563eb',
                                border: hasAny ? '1px solid #334155' : 'none',
                                color: hasAny ? '#cbd5e1' : '#fff',
                                padding: '3px 8px', fontSize: 10, borderRadius: 3,
                                cursor: inflight ? 'wait' : 'pointer',
                              }}>
                              {inflight ? <Loader2 size={10} className="spin" /> : (hasAny ? 'Re-resolve' : 'Resolve')}
                            </button>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={5} style={{ padding: '8px 18px', background: '#020617' }}>
                              {!mentionDetails && (
                                <div style={{ color: '#94a3b8', fontSize: 11 }}>
                                  <Loader2 size={11} className="spin" style={{ display: 'inline', marginRight: 4 }} />
                                  Loading mentions detail…
                                </div>
                              )}
                              {mentionDetails && mentionDetails.length === 0 && (
                                <div style={{ color: '#94a3b8', fontSize: 11, fontStyle: 'italic' }}>No mentions resolved.</div>
                              )}
                              {mentionDetails && mentionDetails.length > 0 && (
                                <>
                                  <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6 }}>
                                    {mentionDetails.length} unique player{mentionDetails.length === 1 ? '' : 's'} resolved across the video:
                                  </div>
                                  <table style={{ width: '100%', fontSize: 10, borderCollapse: 'collapse' }}>
                                    <thead>
                                      <tr style={{ color: '#94a3b8' }}>
                                        <th style={{ padding: '3px 6px', textAlign: 'left' }}>Player</th>
                                        <th style={{ padding: '3px 6px', textAlign: 'left' }}>Team</th>
                                        <th style={{ padding: '3px 6px', textAlign: 'right' }}>#</th>
                                        <th style={{ padding: '3px 6px', textAlign: 'right' }}>Mentions</th>
                                        <th style={{ padding: '3px 6px', textAlign: 'left' }}>Spans (raw STT)</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {mentionDetails.map(p => (
                                        <tr key={p.player_id} style={{ borderTop: '1px solid #0d1622' }}>
                                          <td style={{ padding: '3px 6px', color: '#dbe5f5' }}>
                                            {p.canonical_name || <em style={{ color: '#666' }}>(unknown)</em>}
                                          </td>
                                          <td style={{ padding: '3px 6px', color: '#cbd5e1' }}>{p.team_display_name || '—'}</td>
                                          <td style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace', color: '#888' }}>{p.jersey_number ?? '—'}</td>
                                          <td style={{ padding: '3px 6px', textAlign: 'right', fontFamily: 'monospace', color: '#7dd3fc' }}>{p.mention_count}</td>
                                          <td style={{ padding: '3px 6px', color: '#94a3b8', fontFamily: 'monospace', fontSize: 9 }}>
                                            {(p.spans || []).join(', ')}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          {videos.length === 0 && !err && (
            <div style={{ fontSize: 12, color: '#888' }}>
              {showAll ? 'No indexed videos.' : 'No indexed videos match our current roster coverage. Toggle "Show all" to see the full list.'}
            </div>
          )}
        </>
      )}
    </div>
  )
}

// Step 28A Phase 2c: roster-grounded prompts setting toggle. Read/writes
// the tbtv_ai_roster_grounded_prompts app_setting. Default OFF — flipping
// ON means every TBTV-AI LLM call (FKM/chapters/summarize) gets a "Known
// players in this broadcast" preamble built from cue_entity_mentions +
// teams-data canonical names.
function RosterGroundingToggle() {
  const api = useHighlightsApi()
  const [value, setValue] = useState(null)
  const [busy, setBusy] = useState(false)

  const refresh = useCallback(() => {
    api.listSettings()
      .then(r => {
        const row = (r?.settings || []).find(s => s.key === 'tbtv_ai_roster_grounded_prompts')
        setValue(row?.value || 'false')
      })
      .catch(() => setValue('false'))
  }, [api])

  useEffect(() => { refresh() }, [refresh])

  async function toggle() {
    if (busy) return
    const next = value === 'true' ? 'false' : 'true'
    setBusy(true)
    try {
      await api.setSetting('tbtv_ai_roster_grounded_prompts', next)
      setValue(next)
    } catch (e) {
      alert(`Failed to update setting: ${e?.message || e}`)
    } finally { setBusy(false) }
  }

  const isOn = value === 'true'
  return (
    <div style={{
      marginBottom: 10, padding: '8px 10px', background: '#020617',
      border: '1px solid #1e293b', borderRadius: 4,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Sparkles size={12} style={{ color: isOn ? '#fbbf24' : '#666' }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#dbe5f5' }}>
            Roster-grounded LLM prompts (Phase 2c)
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
            When ON: every TBTV-AI LLM call (Find Key Moments + Chapters + Summarize) gets a "Known players" preamble built from this video's resolved mentions. LLM outputs canonical spellings.
          </div>
        </div>
        <button onClick={toggle} disabled={busy || value === null}
          style={{
            padding: '4px 12px',
            background: isOn ? '#22c55e' : '#334155',
            border: 'none', color: '#fff', borderRadius: 4, fontSize: 11,
            cursor: busy ? 'wait' : 'pointer',
            minWidth: 60,
          }}>
          {busy ? <Loader2 size={11} className="spin" /> : (isOn ? 'ON' : 'OFF')}
        </button>
      </div>
    </div>
  )
}

function pctStr(n) { return n == null ? '—' : (n * 100).toFixed(0) + '%' }

function fmtAgo(iso) {
  if (!iso) return '—'
  const then = new Date(iso.replace(' ', 'T') + 'Z').getTime()
  if (!Number.isFinite(then)) return iso
  const deltaS = Math.floor((Date.now() - then) / 1000)
  if (deltaS < 60) return `${deltaS}s ago`
  if (deltaS < 3600) return `${Math.floor(deltaS / 60)}m ago`
  if (deltaS < 86400) return `${Math.floor(deltaS / 3600)}h ago`
  return `${Math.floor(deltaS / 86400)}d ago`
}

// ==========================================================================
// Sport Profiles admin panel — Phase 4.
//
// Lists every profile in sport_profiles. Editors can inline-edit display_name,
// sort_order, and the enabled + is_default flags; delete a profile (server
// guards against deleting the default or an in-use profile). Creating a
// new profile is deferred to the Phase 5 football rollout because it needs
// a code deploy for the prompt bundle + vocab JSON anyway; adding via
// wrangler d1 INSERT is fine in the meantime.
// ==========================================================================

function SportProfilesPanel() {
  const api = useHighlightsApi()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [busyKey, setBusyKey] = useState(null)
  const [edits, setEdits] = useState({}) // profile_key -> { display_name?, sort_order? }
  const [confirmDelete, setConfirmDelete] = useState(null)

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const res = await api.listProfiles()
      setRows(Array.isArray(res?.profiles) ? res.profiles : [])
    } catch (err) {
      setError(err.message || 'failed to load profiles')
    } finally {
      setLoading(false)
    }
  }, [api])

  useEffect(() => { load() }, [load])

  const patch = useCallback(async (profileKey, body) => {
    setBusyKey(profileKey); setError(null)
    try {
      await api.patchProfile(profileKey, body)
      await load()
      setEdits(e => { const { [profileKey]: _, ...rest } = e; return rest })
    } catch (err) {
      setError(err.message || `failed to update ${profileKey}`)
    } finally {
      setBusyKey(null)
    }
  }, [api, load])

  const del = useCallback(async (profileKey) => {
    setBusyKey(profileKey); setError(null)
    try {
      await api.deleteProfile(profileKey)
      await load()
      setConfirmDelete(null)
    } catch (err) {
      setError(err.message || `failed to delete ${profileKey}`)
    } finally {
      setBusyKey(null)
    }
  }, [api, load])

  const stagedEdit = (profileKey, field, value) => {
    setEdits(e => ({ ...e, [profileKey]: { ...(e[profileKey] || {}), [field]: value } }))
  }

  const saveStaged = (profileKey) => {
    const staged = edits[profileKey]
    if (!staged || Object.keys(staged).length === 0) return
    // Coerce sort_order to number if present.
    const body = { ...staged }
    if ('sort_order' in body) body.sort_order = Number(body.sort_order) || 0
    patch(profileKey, body)
  }

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 15, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Sparkles size={14} /> Sport Profiles
          </h3>
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            Modular per-sport config for the Highlights AI pipeline. Chip vocabulary + AI dispatch prompts + dedup rules per profile.
            Adding a new sport still requires a code deploy for the prompt bundle + vocab JSON; edit here after the bundle lands.
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          style={{ padding: '4px 8px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'var(--text-muted, #888)', cursor: loading ? 'wait' : 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
          title="Reload from D1"
        >
          {loading ? <Loader2 size={12} className="spin" /> : <RefreshCw size={12} />} Refresh
        </button>
      </div>

      {error && (
        <div style={{ padding: 8, background: '#2a1414', border: '1px solid #5a1f1f', borderRadius: 4, color: '#fca5a5', fontSize: 12, marginBottom: 8 }}>
          {error}
        </div>
      )}

      {loading && rows.length === 0 ? (
        <div className="muted" style={{ fontSize: 12 }}>Loading profiles…</div>
      ) : rows.length === 0 ? (
        <div className="muted" style={{ fontSize: 12 }}>No profiles seeded — this should be impossible; run migration 0035 on tbtv-highlights.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--text-muted, #888)' }}>
              <th style={{ padding: 6, borderBottom: '1px solid #333' }}>Key</th>
              <th style={{ padding: 6, borderBottom: '1px solid #333' }}>Display name</th>
              <th style={{ padding: 6, borderBottom: '1px solid #333', textAlign: 'center' }}>Enabled</th>
              <th style={{ padding: 6, borderBottom: '1px solid #333', textAlign: 'center' }}>Default</th>
              <th style={{ padding: 6, borderBottom: '1px solid #333', textAlign: 'right' }}>Chips</th>
              <th style={{ padding: 6, borderBottom: '1px solid #333' }}>Bundles</th>
              <th style={{ padding: 6, borderBottom: '1px solid #333', textAlign: 'right' }}>Order</th>
              <th style={{ padding: 6, borderBottom: '1px solid #333', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(p => {
              const staged = edits[p.profile_key] || {}
              const dirty = Object.keys(staged).length > 0
              const busy = busyKey === p.profile_key
              const displayValue = 'display_name' in staged ? staged.display_name : p.display_name
              const orderValue = 'sort_order' in staged ? staged.sort_order : p.sort_order
              return (
                <tr key={p.profile_key} style={{ borderBottom: '1px solid #1a1a1a' }}>
                  <td style={{ padding: 6, fontFamily: 'monospace', color: 'var(--text-muted, #888)' }}>{p.profile_key}</td>
                  <td style={{ padding: 6 }}>
                    <input
                      type="text"
                      value={displayValue}
                      onChange={e => stagedEdit(p.profile_key, 'display_name', e.target.value)}
                      style={{ width: '100%', background: 'var(--bg-secondary, #1a1a1a)', color: 'var(--text-primary, #fff)', border: '1px solid #333', borderRadius: 4, padding: '4px 6px', fontSize: 12 }}
                    />
                  </td>
                  <td style={{ padding: 6, textAlign: 'center' }}>
                    <button
                      type="button"
                      onClick={() => patch(p.profile_key, { is_enabled: !p.is_enabled })}
                      disabled={busy}
                      style={{
                        padding: '3px 10px',
                        background: p.is_enabled ? '#166534' : '#3f3f46',
                        color: 'white',
                        border: 'none',
                        borderRadius: 4,
                        cursor: busy ? 'wait' : 'pointer',
                        fontSize: 11,
                        fontWeight: 600,
                      }}
                      title={p.is_enabled ? 'Click to disable' : 'Click to enable'}
                    >
                      {busy ? <Loader2 size={10} className="spin" /> : (p.is_enabled ? 'ON' : 'OFF')}
                    </button>
                  </td>
                  <td style={{ padding: 6, textAlign: 'center' }}>
                    <input
                      type="radio"
                      name="sport-profile-default"
                      checked={!!p.is_default}
                      disabled={busy || !!p.is_default}
                      onChange={() => patch(p.profile_key, { is_default: true })}
                      title={p.is_default ? 'This is the site default' : 'Set as site default'}
                    />
                  </td>
                  <td style={{ padding: 6, textAlign: 'right', color: 'var(--text-muted, #888)' }}>
                    {Array.isArray(p.moment_types) ? p.moment_types.length : '—'}
                  </td>
                  <td style={{ padding: 6, fontFamily: 'monospace', color: 'var(--text-muted, #888)', fontSize: 11 }}>
                    <div>{p.prompt_bundle_key}</div>
                    <div>{p.vocab_key}</div>
                  </td>
                  <td style={{ padding: 6, textAlign: 'right' }}>
                    <input
                      type="number"
                      value={orderValue ?? 0}
                      onChange={e => stagedEdit(p.profile_key, 'sort_order', e.target.value)}
                      style={{ width: 60, background: 'var(--bg-secondary, #1a1a1a)', color: 'var(--text-primary, #fff)', border: '1px solid #333', borderRadius: 4, padding: '4px 6px', fontSize: 12, textAlign: 'right' }}
                    />
                  </td>
                  <td style={{ padding: 6, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {dirty && (
                      <button
                        type="button"
                        onClick={() => saveStaged(p.profile_key)}
                        disabled={busy}
                        style={{ padding: '4px 8px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: 4, cursor: busy ? 'wait' : 'pointer', fontSize: 11, marginRight: 4 }}
                      >
                        {busy ? <Loader2 size={10} className="spin" /> : 'Save'}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(p.profile_key)}
                      disabled={busy || p.is_default}
                      title={p.is_default ? 'Cannot delete the default profile' : 'Delete this profile'}
                      style={{ padding: '4px 8px', background: 'transparent', color: p.is_default ? '#555' : '#ef4444', border: `1px solid ${p.is_default ? '#333' : '#5a1f1f'}`, borderRadius: 4, cursor: (busy || p.is_default) ? 'not-allowed' : 'pointer', fontSize: 11 }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      {confirmDelete && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#1a1a1a', border: '1px solid #333', borderRadius: 8, padding: 20, maxWidth: 400 }}>
            <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Delete profile <code>{confirmDelete}</code>?</h4>
            <p className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
              This removes the D1 row. If any content still references this profile_key the server will refuse the delete. Prompt bundle + vocab code files stay on disk (harmless).
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setConfirmDelete(null)} disabled={busyKey === confirmDelete} style={{ padding: '6px 12px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'var(--text-muted, #888)', cursor: 'pointer', fontSize: 12 }}>
                Cancel
              </button>
              <button type="button" onClick={() => del(confirmDelete)} disabled={busyKey === confirmDelete} style={{ padding: '6px 12px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 4, cursor: busyKey === confirmDelete ? 'wait' : 'pointer', fontSize: 12, fontWeight: 600 }}>
                {busyKey === confirmDelete ? <Loader2 size={10} className="spin" /> : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
